import { motion } from "motion/react";
import { Link, type To } from "react-router-dom";
import { CalendarClock, MessageCircle, Wallet } from "lucide-react";
import { EASE_EXPO } from "@/components/motion/primitives";
import { FEES } from "@/data/fees";
import { C } from "./scenes/parts";
import { AppDesk } from "./scenes/CaseDesk";
import { SlideFrame, type Slide } from "./scenes/SlideFrame";

/*
 * What Migrada does, in two pictures, the way Wise does it. The first is the
 * app itself, in use on a phone on a desk (scenes/AppDesk); the second moves
 * through a few photos with a status bar. Then a headline, two lines, one button.
 *
 * The photos are the real ones from the earlier homepage (Unsplash and a
 * stock licence; see public/photos). They are illustrations: never put a
 * name, a quote or a result next to them. The bar under each picture only ever states real app behaviour
 * and real 2026 figures from FEES.
 */

const ICON = "h-5 w-5 sm:h-6 sm:w-6";
const FUNDS = `€${FEES.FINANCIAL_UNDER_24_ANNUAL.toLocaleString("en-GB")}`;

const PEOPLE: Slide[] = [
  {
    id: "students",
    photo: { src: "/photos/students-laptop.jpg", alt: "A student laughs at her laptop while a friend leans in beside her." },
    chip: { icon: <MessageCircle className={ICON} />, label: "New answer", value: "Permit and money" },
  },
  {
    id: "tablet",
    photo: { src: "/photos/student-tablet.jpg", alt: "A student sits on a campus bench with a tablet, smiling." },
    chip: { icon: <Wallet className={ICON} />, label: "To show for a year", value: FUNDS },
  },
  {
    id: "vienna",
    photo: { src: "/photos/vienna-cafe.jpg", alt: "A Vienna café in the afternoon sun, the Votivkirche through the window." },
    chip: { icon: <CalendarClock className={ICON} />, label: "Meldezettel", value: "in 3 days" },
  },
];

const SECTIONS: {
  id: string;
  title: string;
  body: string;
  cta: { label: string; to: To };
  link: { label: string; to: To };
  media: React.ReactNode;
}[] = [
  {
    id: "your-move",
    title: "Your whole move, in one place.",
    body: "Your documents checked in seconds, every date counted down and a person to ask when something is unclear, from the first question to your first week in Austria.",
    cta: { label: "Get visa help", to: "/start" },
    link: { label: "Read the FAQ", to: "/faq" },
    media: <AppDesk />,
  },
  {
    id: "people",
    title: "People who did it, on your side.",
    body: "Ask in the community and students who made the move answer, usually within 48 hours. Parents are welcome to ask too.",
    cta: { label: "Visit the community", to: "/community" },
    link: { label: "Answers for parents", to: { pathname: "/", hash: "#parents" } },
    media: <SlideFrame slides={PEOPLE} label="A student laughing at her laptop with a friend, a student with a tablet on campus and a Vienna café with the Votivkirche through the window." />,
  },
];

export function MoveSections() {
  return (
    <div style={{ backgroundColor: "#F2F2ED" }}>
      {SECTIONS.map((s, i) => (
        <section key={s.id} id={s.id} className="scroll-mt-24 py-14 sm:py-20 lg:py-24">
          <div className="mx-auto grid max-w-[1180px] grid-cols-1 items-center gap-9 px-5 sm:px-8 lg:grid-cols-2 lg:gap-20">
            <motion.div
              className={`mx-auto w-full max-w-[540px] ${i % 2 ? "lg:order-2" : ""}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.9, ease: EASE_EXPO }}
            >
              {s.media}
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: 0.1, ease: EASE_EXPO }}
            >
              <h2 className="max-w-[14ch] font-black leading-[1.02] tracking-[-0.04em]" style={{ fontSize: "clamp(2.2rem, 1.5rem + 2.6vw, 3.75rem)", color: C.INK }}>
                {s.title}
              </h2>
              <p className="mt-5 max-w-[32rem] text-[18px] leading-relaxed sm:text-[20px]" style={{ color: C.SECONDARY }}>
                {s.body}
              </p>
              <div className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4">
                <Link to={s.cta.to} className="rounded-full px-8 py-4 text-[17px] font-bold transition-transform hover:-translate-y-0.5" style={{ backgroundColor: C.TEAL, color: C.BRAND_DARK }}>
                  {s.cta.label}
                </Link>
                <Link to={s.link.to} className="text-[17px] font-bold underline decoration-2 underline-offset-[6px] hover:decoration-[#2EE6D6]" style={{ color: C.BRAND_DARK }}>
                  {s.link.label}
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      ))}
    </div>
  );
}
